// frontend/shared-ui/src/forms/schemas/closure.js

// ─── Codes for SKU generation ────────────────────────────────────────
const CLOSURE_TYPE_CODES = {
    'Inline Closure': 'ILC',
    'Dome Closure': 'DMC',
    'Horizontal Closure': 'HRC',
    'Vertical Closure': 'VTC',
    'Fiber Optic Splice Joint': 'SJT',
    'Terminal Box': 'TRM',
    'Butt Closure': 'BTC',
};

const CLOSURE_TECH_CODES = {
    'Heat-Shrink': 'HS',
    'Mechanical': 'MEC',
    'Cold Seal': 'CS',
    'Gel Seal': 'GEL',
    'Pre-terminated': 'PRE',
};

const MOUNT_CODES = {
    'Aerial': 'AER',
    'Buried': 'BUR',
    'Manhole': 'MAN',
    'Wall Mount': 'WM',
    'Pole Mount': 'PM',
    'Duct': 'DUCT',
};

const MATERIAL_CODES = {
    'PP (Polypropylene)': 'PP',
    'PC (Polycarbonate)': 'PC',
    'ABS': 'ABS',
    'Stainless Steel': 'SS',
    'Aluminum': 'AL',
};

export default {
    slug: 'closure',
    label: 'Splice Closures & Joints',
    defaultUnit: 'pcs',
    costDefaults: {
        avgVolumeCbm: 0.035,
        suggestedMarkupPct: 35,
        freightSensitivity: 'high',
        note: 'Small inline 0.018, dome 0.034, large dome 0.063',
    },

    groups: [
        // ──────────────────────────────────────────────────────────────
        // 1. Basic
        // ──────────────────────────────────────────────────────────────
        {
            id: 'basic',
            label: 'Basic',
            columns: 2,
            fields: [
                {
                    key: 'closureType',
                    label: 'Closure Type',
                    type: 'select',
                    required: true,
                    options: [
                        'Inline Closure',
                        'Dome Closure',
                        'Horizontal Closure',
                        'Vertical Closure',
                        'Fiber Optic Splice Joint',
                        'Terminal Box',
                        'Butt Closure',
                    ],
                },
                {
                    key: 'capacity',
                    label: 'Splice Capacity (cores)',
                    type: 'quick-qty',
                    required: true,
                    min: 1,
                    unitOptions: ['core', 'cores'],
                    presets: [4, 8, 12, 24, 48, 96, 144, 288],
                    hint: 'Maximum number of splices this closure can hold',
                },
            ],
        },

        // ──────────────────────────────────────────────────────────────
        // 2. Sealing & protection
        // ──────────────────────────────────────────────────────────────
        {
            id: 'sealing',
            label: 'Sealing & Protection',
            columns: 2,
            fields: [
                {
                    key: 'sealingType',
                    label: 'Sealing / Splicing Technology',
                    type: 'select',
                    required: true,
                    options: [
                        'Heat-Shrink',
                        'Mechanical',
                        'Cold Seal',
                        'Gel Seal',
                        'Pre-terminated',
                    ],
                },
                {
                    key: 'protectionRating',
                    label: 'IP Rating',
                    type: 'select',
                    options: ['IP54', 'IP65', 'IP66', 'IP67', 'IP68'],
                },
                {
                    key: 'cablePorts',
                    label: 'Cable Ports',
                    type: 'number',
                    min: 0,
                    hint: 'Number of cable entry ports',
                },
                {
                    key: 'cableDiameterMin_mm',
                    label: 'Cable Diameter — Min',
                    type: 'number',
                    unit: 'mm',
                    step: 0.1,
                },
                {
                    key: 'cableDiameterMax_mm',
                    label: 'Cable Diameter — Max',
                    type: 'number',
                    unit: 'mm',
                    step: 0.1,
                },
                {
                    key: 'reEnterable',
                    label: 'Re-enterable',
                    type: 'boolean',
                    hint: 'Can the closure be reopened for future maintenance?',
                },
            ],
        },

        // ──────────────────────────────────────────────────────────────
        // 3. Installation
        // ──────────────────────────────────────────────────────────────
        {
            id: 'installation',
            label: 'Installation',
            columns: 2,
            fields: [
                {
                    key: 'mountType',
                    label: 'Mounting / Installation',
                    type: 'select',
                    options: ['Aerial', 'Buried', 'Manhole', 'Wall Mount', 'Pole Mount', 'Duct'],
                },
                {
                    key: 'material',
                    label: 'Material',
                    type: 'select',
                    options: [
                        'PP (Polypropylene)',
                        'PC (Polycarbonate)',
                        'ABS',
                        'Stainless Steel',
                        'Aluminum',
                    ],
                },
                {
                    key: 'spliceTrayCount',
                    label: 'Splice Trays',
                    type: 'number',
                    min: 0,
                    hint: 'Number of trays included',
                },
                {
                    key: 'coresPerTray',
                    label: 'Cores per Tray',
                    type: 'number',
                    min: 0,
                },
                {
                    key: 'height_mm',
                    label: 'Height',
                    type: 'number',
                    unit: 'mm',
                },
                {
                    key: 'width_mm',
                    label: 'Width',
                    type: 'number',
                    unit: 'mm',
                },
                {
                    key: 'depth_mm',
                    label: 'Depth',
                    type: 'number',
                    unit: 'mm',
                },
            ],
        },
    ],

    // ─── SKU generation ─────────────────────────────────────────────────
    buildSku(v) {
        const typeCode = CLOSURE_TYPE_CODES[v.closureType] || 'CLO';
        const cores = v.capacity ? String(v.capacity).replace(/[^0-9]/g, '') : '';
        const techCode = CLOSURE_TECH_CODES[v.sealingType] || '';
        const mountCode = MOUNT_CODES[v.mountType] || '';
        const matCode = MATERIAL_CODES[v.material] || '';

        const parts = ['CLO', typeCode, cores, techCode, mountCode, matCode].filter(Boolean);
        return parts.join('-').toUpperCase().replace(/\s+/g, '');
    },

    // ─── Name generation ────────────────────────────────────────────────
    buildName(v) {
        const bits = [];

        if (v.capacity) {
            const n = String(v.capacity).replace(/[^0-9]/g, '');
            if (n) bits.push(`${n}-Core`);
        }

        if (v.closureType) bits.push(v.closureType);

        if (v.sealingType) bits.push(`(${v.sealingType})`);

        if (v.mountType) bits.push(`— ${v.mountType}`);

        if (v.protectionRating) bits.push(v.protectionRating);

        return bits.filter(Boolean).join(' ').trim() || 'Splice Closure';
    },

    // ─── Detail view ────────────────────────────────────────────────────
    attributeSchema: [
        { key: 'closureType', label: 'Closure Type' },
        { key: 'capacity', label: 'Splice Capacity', unit: 'cores' },
        { key: 'sealingType', label: 'Sealing Technology' },
        { key: 'protectionRating', label: 'IP Rating' },
        { key: 'cablePorts', label: 'Cable Ports' },
        { key: 'cableDiameterMin_mm', label: 'Cable Diameter Min', unit: 'mm' },
        { key: 'cableDiameterMax_mm', label: 'Cable Diameter Max', unit: 'mm' },
        { key: 'reEnterable', label: 'Re-enterable' },
        { key: 'mountType', label: 'Mounting' },
        { key: 'material', label: 'Material' },
        { key: 'spliceTrayCount', label: 'Splice Trays' },
        { key: 'coresPerTray', label: 'Cores per Tray' },
        { key: 'height_mm', label: 'Height', unit: 'mm' },
        { key: 'width_mm', label: 'Width', unit: 'mm' },
        { key: 'depth_mm', label: 'Depth', unit: 'mm' },
    ],
};